import { DateValue } from '@internationalized/date';
import { RangeValue } from '@react-types/shared';
import { groupBy, sortBy } from 'lodash/fp';
import { DateTime } from 'luxon';
import { useCallback, useEffect } from 'react';
import { connect, ConnectedProps } from 'react-redux';
import { useNavigate } from 'react-router-dom-v5-compat';

import { FilterTagsCondition } from 'dashboard/components/TagsConditionFilter';
import { BulkSetCaseStateData } from 'dashboard/forms/FormBulkSetCaseState';
import SessionClientDashboard from 'dashboard/lib/clients/SessionClientDashboard';
import { MessageType } from 'dashboard/lib/interfaces/Message';
import { productFeatureDisabled } from 'dashboard/lib/productFeature';
import { Case } from 'dashboard/models/case';
import { CaseTemplateStatus } from 'dashboard/models/case-template';
import { ExporterFileType } from 'dashboard/models/exporter';
import { ProductFeatureName } from 'dashboard/models/organization';
import {
  CasesTableView,
  generateCaseFiltersFromTableView,
  TableKey,
  TableViewStatus,
} from 'dashboard/models/table-view';
import {
  USER_NOTIFICATIONS_CASES,
  UserNotification,
  UserNotificationStatus,
} from 'dashboard/models/user-notification';
import {
  FilterValues,
  setCurrentPage,
  setFilters,
  setPageSize,
  setSortParams,
} from 'dashboard/redux/actions/cases';
import { showMessage } from 'dashboard/redux/actions/messages';
import { useListUsersQuery } from 'dashboard/redux/api/users';
import { getAdjustedCaseFilters } from 'dashboard/redux/selectors/case';
import { setCurrentTableViewId, setTableViews } from 'dashboard/redux/slices/table-views';
import {
  clearUserNotifications,
  saveUserNotifications,
} from 'dashboard/redux/slices/user-notifications';
import { RootState } from 'dashboard/redux/store';
import { createCase } from 'dashboard/redux/thunk-actions/case';
import { loadCaseQueuesList } from 'dashboard/redux/thunk-actions/case-queues';
import { loadCaseTemplateBasicsOptions } from 'dashboard/redux/thunk-actions/case-templates';
import { loadCasesList, loadExporterCase } from 'dashboard/redux/thunk-actions/cases';
import { loadTableViews } from 'dashboard/redux/thunk-actions/table-views';
import { loadTags } from 'dashboard/redux/thunk-actions/tags';
import { loadTeams } from 'dashboard/redux/thunk-actions/teams';
import {
  loadUserNotificationsList,
  loadUserNotificationsListAdd,
} from 'dashboard/redux/thunk-actions/user-notifications';
import DashboardCasesView from 'dashboard/views/DashboardCasesView';
import { CreateCaseFormData } from 'dashboard/views/DashboardCasesView/CasesCreateCaseModal';
import {
  ARCHIVED_REQUEST_KEY,
  INBOX_REQUEST_KEY,
  RequestKey,
} from 'dashboard/views/DashboardCasesView/constants';
import useApiCallback from 'lib/hooks/useApiCallback';
import useApiErrorHandler from 'lib/hooks/useApiErrorHandler';
import useApiLoader from 'lib/hooks/useApiLoader';
import useInterval from 'lib/hooks/useInterval';
import usePermission from 'lib/hooks/usePermission';
import useRTKErrorHandler from 'lib/hooks/useRTKErrorHandler';
import useTable from 'lib/hooks/useTable';
import { ApiEndpoint, ApiModel } from 'lib/interfaces/api';
import { Permission } from 'lib/permissions';
import { isPermissionsError, permissionsErrorMessage } from 'lib/permissions-errors';
import { Iso8601DateString } from 'lib/types';
import { getDateRange, updateDateFilters } from 'lib/utils/filters';

type TableViewConfiguration = CasesTableView['attributes']['configuration'];

const DashboardCasesRoute = (props: PropsFromRedux) => {
  const navigate = useNavigate();
  const productFeatureIsDisabled = productFeatureDisabled(
    props.organization,
    ProductFeatureName.CasesDashboard,
    props.currentEnvironment.type,
  );
  const exportersProductFeatureIsDisabled = productFeatureDisabled(
    props.organization,
    ProductFeatureName.Exporters,
    props.currentEnvironment.type,
  );
  const { activeCaseTemplateBasicsOptions } = props;

  const hasTeamViewPermission = usePermission(Permission.TeamView);
  const hasCaseQueueViewPermission = usePermission(Permission.CaseQueueView);

  // use RTK query to leverage caching
  const { data: users, error: listUsersError } = useListUsersQuery();

  useRTKErrorHandler({
    error: listUsersError,
    onError: (err) => {
      switch (err.status) {
        case 401:
          return {
            message:
              'Your role does not have permissions to view users in this organization. You cannot assign cases.',
            level: MessageType.Notice,
          };
        default:
          return {
            message: 'Failed to load users.',
            level: MessageType.Failure,
          };
      }
    },
  });

  useApiLoader({
    loader: () => props.loadTeams(),
    deps: [props.loadTeams],
    skip: !hasTeamViewPermission,
    onError: () => {
      props.showMessage('Failed to load teams.', MessageType.Failure);
    },
    onNetworkError: () =>
      props.showMessage(
        'We are experiencing network issues. Please try again.',
        MessageType.Failure,
      ),
  });

  const { loading: loadingTableViews, callback: loadCaseTableViews } = useApiLoader({
    loader: () =>
      props.loadTableViews({
        filters: { tableKey: 'cases_index', status: [TableViewStatus.Active] },
        sortParams: { sort: 'name', shouldSortDescending: false },
      }),
    deps: [],
    onError: (error) => {
      if (isPermissionsError(error)) {
        props.showMessage(permissionsErrorMessage(error), MessageType.Failure);
      } else {
        props.showMessage('Failed to load table views.', MessageType.Failure);
      }
    },
    onNetworkError: () => {
      props.showMessage(
        'We are experiencing network issues. Please try again.',
        MessageType.Failure,
      );
    },
  });

  const { callback: loadUnreadUserNotifications } = useApiLoader({
    loader: () =>
      props.loadUserNotificationsList({
        categories: USER_NOTIFICATIONS_CASES,
        status: UserNotificationStatus.Delivered,
        requestKey: INBOX_REQUEST_KEY,
      }),
    deps: [props.loadUserNotificationsList],
    onError: (error) => {
      if (isPermissionsError(error)) {
        props.showMessage(permissionsErrorMessage(error), MessageType.Failure);
      } else {
        props.showMessage('Failed to load notifications.', MessageType.Failure);
      }
    },
    onNetworkError: () =>
      props.showMessage(
        'We are experiencing network issues. Please try again.',
        MessageType.Failure,
      ),
  });

  const { callback: loadArchivedUserNotifications } = useApiLoader({
    loader: () =>
      props.loadUserNotificationsList({
        categories: USER_NOTIFICATIONS_CASES,
        status: UserNotificationStatus.Archived,
        requestKey: ARCHIVED_REQUEST_KEY,
      }),
    deps: [props.loadUserNotificationsList],
    onError: (error) => {
      if (isPermissionsError(error)) {
        props.showMessage(permissionsErrorMessage(error), MessageType.Failure);
      } else {
        props.showMessage('Failed to load notifications.', MessageType.Failure);
      }
    },
    onNetworkError: () =>
      props.showMessage(
        'We are experiencing network issues. Please try again.',
        MessageType.Failure,
      ),
  });

  const { loading: loadingTags } = useApiLoader({
    loader: () => props.loadTags(ApiModel.Case),
    deps: [props.loadTags, props.loadCasesList],
    onError: (error) => {
      if (isPermissionsError(error)) {
        props.showMessage(permissionsErrorMessage(error), MessageType.Failure);
      } else {
        props.showMessage('Failed to load tags.', MessageType.Failure);
      }
    },
    onNetworkError: () =>
      props.showMessage(
        'We are experiencing network issues. Please try again.',
        MessageType.Failure,
      ),
  });

  const { loading: loadingCaseTemplateBasicsOptions } = useApiLoader({
    loader: props.loadCaseTemplateBasicsOptions,
    deps: [props.loadCaseTemplateBasicsOptions],
    /* @ts-expect-error [strict-migration] The following line is not strict; please fix! */
    onError: null,
    /* @ts-expect-error [strict-migration] The following line is not strict; please fix! */
    onNetworkError: null,
  });

  useApiLoader({
    loader: () =>
      props.loadCaseQueuesList({
        currentPage: 1,
        pageSize: 100,
        sortParams: { sort: '-', shouldSortDescending: false },
        filters: { status: ['active'] },
      }),
    deps: [props.loadCaseQueuesList],
    skip: !hasCaseQueueViewPermission,
    onError: () => {
      props.showMessage('Failed to load case queues.', MessageType.Failure);
    },
    onNetworkError: () =>
      props.showMessage(
        'We are experiencing network issues. Please try again.',
        MessageType.Failure,
      ),
  });

  // Note: Filter on initial case state ids and last 2 weeks by default and we want to call this only once on page loading
  useEffect(() => {
    if (
      loadingTableViews ||
      activeCaseTemplateBasicsOptions.length === 0 ||
      !props.filters.isInitial
    ) {
      return;
    }

    if (props.currentTableView) {
      props.setFilters({
        ...generateCaseFiltersFromTableView(
          props.currentTableView.attributes.configuration.filters,
        ),
        isInitial: false,
      });
      if (props.currentTableView.attributes.configuration.sort) {
        props.setSortParams({
          sort: props.currentTableView.attributes.configuration.sort.sort,
          shouldSortDescending:
            props.currentTableView.attributes.configuration.sort['should-sort-descending'],
        });
      }
    } else {
      const initialStates = activeCaseTemplateBasicsOptions.map((filterOption) =>
        filterOption.states.find((state) => state.token === filterOption['initial-state-token']),
      );
      const stateGroup = groupBy('name', initialStates);
      const initialStateNames = Object.keys(stateGroup);

      const now = DateTime.local();
      const startOfDay = now.startOf('day');
      const endOfDay = startOfDay.endOf('day');
      const startTimeRange = startOfDay.minus({ days: 14 });

      props.setFilters({
        caseStateNames: initialStateNames,
        startDate: startTimeRange.toISO(),
        endDate: endOfDay.toISO(),
        isInitial: false,
      });
    }
  }, [activeCaseTemplateBasicsOptions, loadingTableViews]);

  const casesLoader = () =>
    props.loadCasesList({
      currentEnvironment: props.currentEnvironment,
      /* @ts-expect-error [strict-migration] The following line is not strict; please fix! */
      currentPage: props.currentPage,
      /* @ts-expect-error [strict-migration] The following line is not strict; please fix! */
      pageSize: props.pageSize,
      /* @ts-expect-error [strict-migration] The following line is not strict; please fix! */
      sortParams: props.sortParams,
      filters: props.filters,
    });
  const casesLoaderDependencies = [
    props.loadCasesList,
    props.currentEnvironment,
    props.currentPage,
    props.pageSize,
    props.sortParams,
    props.filters,
  ];

  const { loading: loadingCases } = useApiLoader({
    loader: casesLoader,
    deps: casesLoaderDependencies,
    onError: (error) => {
      if (isPermissionsError(error)) {
        props.showMessage(permissionsErrorMessage(error), MessageType.Failure);
      } else {
        props.showMessage('Failed to load cases.', MessageType.Failure);
      }
    },
    onNetworkError: () =>
      props.showMessage(
        'We are experiencing network issues. Please try again.',
        MessageType.Failure,
      ),
  });

  // NOTE: we use a new callback function so that `loadingCases` is not updated
  // when we poll for cases.
  const { callback: pollCases, error: pollCasesError } = useApiCallback(async () => {
    props.loadCasesList({
      currentEnvironment: props.currentEnvironment,
      /* @ts-expect-error [strict-migration] The following line is not strict; please fix! */
      currentPage: props.currentPage,
      /* @ts-expect-error [strict-migration] The following line is not strict; please fix! */
      pageSize: props.pageSize,
      /* @ts-expect-error [strict-migration] The following line is not strict; please fix! */
      sortParams: props.sortParams,
      filters: props.filters,
      isPollRequest: true,
    });
  }, [
    props.loadCasesList,
    props.currentEnvironment,
    props.currentPage,
    props.pageSize,
    props.sortParams,
    props.filters,
  ]);

  useApiErrorHandler({
    error: pollCasesError,
    onError: (error) => {
      if (isPermissionsError(error)) {
        props.showMessage(permissionsErrorMessage(error), MessageType.Failure);
      } else {
        props.showMessage('Failed to load cases.', MessageType.Failure);
      }
    },
    onNetworkError: () =>
      props.showMessage(
        'We are experiencing network issues. Please try again.',
        MessageType.Failure,
      ),
  });

  useInterval({ callback: pollCases, delay: 5000, pauseWhenBackgrounded: true });

  const { error: handleCreateCaseError, callback: handleCreateCase } = useApiCallback(
    async (values: CreateCaseFormData) => {
      const response = await props.createCase({
        environmentId: props.currentEnvironment.id,
        caseTemplateId: values.caseTemplateId,
      });
      props.showMessage('Case created.', MessageType.Success, false);
      navigate(`/dashboard/cases/${response.data.id}`);
    },
    [props.showMessage, props.currentEnvironment, props.createCase],
  );

  useApiErrorHandler({
    error: handleCreateCaseError,
    onError: () =>
      props.showMessage('Failed to create case. Please try again.', MessageType.Failure, false),
    onNetworkError: () =>
      props.showMessage(
        'We are experiencing network issues. Please try again.',
        MessageType.Failure,
        false,
      ),
  });

  const { error: handleExportDataError, callback: handleExportData } = useApiCallback(
    async (fileType: ExporterFileType) => {
      await props.loadExporterCase({
        currentEnvironment: props.currentEnvironment,
        filters: props.filters,
        /* @ts-expect-error [strict-migration] The following line is not strict; please fix! */
        sortParams: props.sortParams,
        fileType: fileType,
      });
      props.showMessage('You will receive an email when your export is ready.', MessageType.Notice);
    },
    [props.currentEnvironment, props.sortParams, props.filters],
  );

  useApiErrorHandler({
    error: handleExportDataError,
    onError: (error) => {
      if (isPermissionsError(error)) {
        props.showMessage(permissionsErrorMessage(error), MessageType.Failure);
      } else if (error.status === 422) {
        props.showMessage(error?.errors?.[0].details as string, MessageType.Failure, false);
      } else {
        props.showMessage('Failed to create export, please try again.', MessageType.Failure, false);
      }
    },
    onNetworkError: () =>
      props.showMessage(
        'We are experiencing network issues. Please try again.',
        MessageType.Failure,
      ),
  });

  const { error: loadUserNotificationPageError, callback: loadUserNotificationPage } =
    useApiCallback(
      async (pageNumber: number, status: UserNotificationStatus, requestKey: RequestKey) => {
        await props.loadUserNotificationsListAdd({
          categories: USER_NOTIFICATIONS_CASES,
          currentPage: pageNumber,
          status,
          requestKey,
        });
      },
      [props.loadUserNotificationsList],
    );

  useApiErrorHandler({
    error: loadUserNotificationPageError,
    onError: () => null, // we don't want to expose this error to the user
    onNetworkError: () => null, // we don't want to expose this error to the user
  });

  const { error: markAllNotificationsAsReceivedError, callback: markAllNotificationsAsReceived } =
    useApiCallback(async () => {
      await SessionClientDashboard.postResourceSingletonAction<UserNotification>(
        { type: ApiModel.UserNotification },
        'mark-all-as-received',
      );
    }, []);

  useApiErrorHandler({
    error: markAllNotificationsAsReceivedError,
    onError: () => null, // we don't want to expose this error to the user
    onNetworkError: () => null, // we don't want to expose this error to the user
  });

  const { error: archiveNotificationError, callback: archiveNotification } = useApiCallback(
    async (notificationId: string) => {
      const { data: updatedNotification } =
        await SessionClientDashboard.postResourceAction<UserNotification>(
          { type: ApiModel.UserNotification, id: notificationId },
          'archive',
        );

      const inboxDetails = props.userNotifications[INBOX_REQUEST_KEY];
      const archivedDetails = props.userNotifications[ARCHIVED_REQUEST_KEY];

      if (inboxDetails && archivedDetails) {
        // Remove notification from INBOX_REQUEST_KEY details
        const nextInboxNotifications = inboxDetails.userNotifications.filter(
          (notif) => notif.id !== notificationId,
        );
        const inboxPaginationParams = inboxDetails.paginationParams;
        const nextInboxPaginationParams = inboxPaginationParams
          ? {
              ...inboxPaginationParams,
              'total-count': inboxPaginationParams['total-count'] - 1,
            }
          : undefined;

        props.saveUserNotifications({
          userNotifications: nextInboxNotifications,
          requestKey: INBOX_REQUEST_KEY,
          paginationParams: nextInboxPaginationParams,
        });

        // Add notification to ARCHIVED_REQUEST_KEY details
        // We sort notifications in the UserNotificationsList component
        const nextArchivedNotifications = [
          ...archivedDetails.userNotifications,
          updatedNotification,
        ];
        const archivedPaginationParams = archivedDetails.paginationParams;
        const nextArchivedPaginationParams = archivedPaginationParams
          ? {
              ...archivedPaginationParams,
              'total-count': archivedPaginationParams['total-count'] + 1,
            }
          : undefined;

        props.saveUserNotifications({
          userNotifications: nextArchivedNotifications,
          requestKey: ARCHIVED_REQUEST_KEY,
          paginationParams: nextArchivedPaginationParams,
        });
      }
    },
    [props.saveUserNotifications, props.userNotifications],
  );

  useApiErrorHandler({
    error: archiveNotificationError,
    onError: (error) => {
      if (isPermissionsError(error)) {
        showMessage(permissionsErrorMessage(error), MessageType.Failure);
      } else {
        showMessage(
          'Failed to acknowledge notification, please try again.',
          MessageType.Failure,
          false,
        );
      }
    },
    onNetworkError: () =>
      showMessage('We are experiencing network issues. Please try again.', MessageType.Failure),
  });

  const { error: unarchiveNotificationError, callback: unarchiveNotification } = useApiCallback(
    async (notificationId: string) => {
      const { data: updatedNotification } =
        await SessionClientDashboard.postResourceAction<UserNotification>(
          { type: ApiModel.UserNotification, id: notificationId },
          'unarchive',
        );

      const inboxDetails = props.userNotifications[INBOX_REQUEST_KEY];
      const archivedDetails = props.userNotifications[ARCHIVED_REQUEST_KEY];

      if (inboxDetails && archivedDetails) {
        // Remove notification from ARCHIVED_REQUEST_KEY details
        const nextArchivedNotifications = archivedDetails.userNotifications.filter(
          (notif) => notif.id !== notificationId,
        );
        const archivedPaginationParams = archivedDetails.paginationParams;
        const nextArchivedPaginationParams = archivedPaginationParams
          ? {
              ...archivedPaginationParams,
              'total-count': archivedPaginationParams['total-count'] - 1,
            }
          : undefined;

        props.saveUserNotifications({
          userNotifications: nextArchivedNotifications,
          requestKey: ARCHIVED_REQUEST_KEY,
          paginationParams: nextArchivedPaginationParams,
        });

        // Add notification to INBOX_REQUEST_KEY details
        // We sort notifications in the UserNotificationsList component
        const nextInboxNotifications = [...inboxDetails.userNotifications, updatedNotification];
        const inboxPaginationParams = inboxDetails.paginationParams;
        const nextInboxPaginationParams = inboxPaginationParams
          ? {
              ...inboxPaginationParams,
              'total-count': inboxPaginationParams['total-count'] + 1,
            }
          : undefined;

        props.saveUserNotifications({
          userNotifications: nextInboxNotifications,
          requestKey: INBOX_REQUEST_KEY,
          paginationParams: nextInboxPaginationParams,
        });
      }
    },
    [props.saveUserNotifications, props.userNotifications],
  );

  useApiErrorHandler({
    error: unarchiveNotificationError,
    onError: (error) => {
      if (isPermissionsError(error)) {
        showMessage(permissionsErrorMessage(error), MessageType.Failure);
      } else {
        showMessage(
          'Failed to acknowledge notification, please try again.',
          MessageType.Failure,
          false,
        );
      }
    },
    onNetworkError: () =>
      showMessage('We are experiencing network issues. Please try again.', MessageType.Failure),
  });

  const { error: archiveAllNotificationsError, callback: archiveAllNotifications } =
    useApiCallback(async () => {
      await SessionClientDashboard.postResourceSingletonAction<UserNotification>(
        { type: ApiModel.UserNotification },
        'mark-all-as-archived',
      );
      props.clearUserNotifications();
      loadUnreadUserNotifications();
      loadArchivedUserNotifications();
    }, [props.clearUserNotifications, loadUnreadUserNotifications, loadArchivedUserNotifications]);

  useApiErrorHandler({
    error: archiveAllNotificationsError,
    onError: (error) => {
      if (isPermissionsError(error)) {
        props.showMessage(permissionsErrorMessage(error), MessageType.Failure);
      } else {
        showMessage(
          'Failed to acknowledge notifications, please try again.',
          MessageType.Failure,
          false,
        );
      }
    },
    onNetworkError: () =>
      showMessage('We are experiencing network issues. Please try again.', MessageType.Failure),
  });

  const { error: bulkAssignCasesError, callback: handleBulkAssign } = useApiCallback(
    async (caseIds: string[], userId: string, message: string) => {
      // If we are passed an undefined userId, we take that to mean unassigned.
      // To prevent axios from stripping that from the payload, we need to set it to null
      // See https://github.com/axios/axios/issues/1139
      const assigneeId = userId != null ? userId : null;
      await SessionClientDashboard.postResourceSingletonAction<Case>(
        { type: ApiModel.Case },
        'bulk-assign',
        {
          meta: {
            'assignee-id': assigneeId,
            'case-ids': caseIds,
            message: message,
          },
        },
      );

      casesLoader();
      const displayMessage =
        caseIds.length > 50
          ? 'Successfully started assigning cases in the background.'
          : caseIds.length > 1
            ? 'Successfully assigned Cases.'
            : 'Successfully assigned Case.';
      props.showMessage(displayMessage, MessageType.Success);
    },
    [casesLoader],
  );

  useApiErrorHandler({
    error: bulkAssignCasesError,
    onError: (error) => {
      if (isPermissionsError(error)) {
        props.showMessage(permissionsErrorMessage(error), MessageType.Failure);
      } else {
        props.showMessage('Failed to assign, please try again.', MessageType.Failure, false);
      }
    },
    onNetworkError: () =>
      props.showMessage('Failed to assign, please try again.', MessageType.Failure, false),
  });

  const { handlePageLinkClick, handlePageSizeClick, handleOmniSearch, handleSortUpdate } = useTable(
    {
      filters: props.filters,
      sortParams: props.sortParams,
      pageSize: props.pageSize,
      currentPage: props.currentPage,
      setFilters: props.setFilters,
      setCurrentPage: props.setCurrentPage,
      setPageSize: props.setPageSize,
      setSortParams: props.setSortParams,
    },
  );

  const handleDateUpdate = useCallback(
    (
      range: { startDate?: Iso8601DateString; endDate?: Iso8601DateString },
      dateType?: 'created_at' | 'resolved_at',
    ) => {
      const { start, end } = getDateRange(range.startDate, range.endDate);
      props.setFilters({
        startDate: start,
        endDate: end,
        dateType,
      });
    },
    [],
  );

  const handleDateUpdateV2 = useCallback(
    (range: RangeValue<DateValue> | null, dateType?: 'created_at' | 'resolved_at') => {
      updateDateFilters(range, (newDateRange) => {
        return handleDateUpdate(newDateRange, dateType);
      });
    },
    [handleDateUpdate],
  );

  const handleDateTypeFilterUpdate = (dateType: 'created_at' | 'resolved_at') => {
    props.setFilters({
      dateType: dateType,
      startDate: props.filters.startDate,
      endDate: props.filters.endDate,
    });
    handlePageLinkClick(1);
  };

  const handleStateFilterUpdate = (stateNames: FilterValues) => {
    props.setFilters({ caseStateNames: stateNames });
    handlePageLinkClick(1);
  };

  const handleCaseTemplateFilterUpdate = (templateIds: FilterValues) => {
    props.setFilters({ caseTemplateIds: templateIds });
    handlePageLinkClick(1);
  };

  const handleCaseQueueFilterUpdate = (caseQueueIds: FilterValues) => {
    props.setFilters({ caseQueueIds });
    handlePageLinkClick(1);
  };

  const handleAssigneeFilterUpdate = (
    userAssigneeIds: FilterValues,
    teamAssigneeIds: FilterValues,
  ) => {
    props.setFilters({ assigneeIds: userAssigneeIds, teamAssigneeIds });
    handlePageLinkClick(1);
  };

  const handleNotAssigneeFilterUpdate = (userAssigneeIds: string[]) => {
    props.setFilters({ notAssigneeIds: userAssigneeIds });
    handlePageLinkClick(1);
  };

  const handleTagsConditionsFilterUpdate = (tagsConditions: FilterTagsCondition[][]) => {
    props.setFilters({ tagsConditions: tagsConditions });
    handlePageLinkClick(1);
  };

  const { error: bulkSetStateError, callback: handleBulkSetState } = useApiCallback(
    async (caseIds: string[], data: BulkSetCaseStateData) => {
      await SessionClientDashboard.postResourceSingletonAction<Case>(
        { type: ApiModel.Case },
        'bulk-set-state',
        {
          meta: {
            'case-ids': caseIds,
            state_id: data.stateId,
            /* @ts-expect-error [strict-migration] The following line is not strict; please fix! */
            message: data.message,
          },
        },
      );

      casesLoader();
      const displayMessage =
        caseIds.length > 50
          ? 'Successfully started updating cases status in the background.'
          : caseIds.length > 1
            ? 'Successfully updated case statuses.'
            : 'Successfully updated case status.';
      props.showMessage(displayMessage, MessageType.Success);
    },
    [casesLoader],
  );

  useApiErrorHandler({
    error: bulkSetStateError,
    onError: (error) => {
      if (isPermissionsError(error)) {
        props.showMessage(permissionsErrorMessage(error), MessageType.Failure);
      } else if (error.status === 403) {
        props.showMessage(
          'Failed to update case status. Please complete the checklist first.',
          MessageType.Failure,
        );
      } else {
        props.showMessage(
          'Failed to start updating case statuses, please try again.',
          MessageType.Failure,
          false,
        );
      }
    },
    onNetworkError: () =>
      props.showMessage(
        'We are experiencing network issues. Please try again.',
        MessageType.Failure,
        false,
      ),
  });

  const { error: createTableViewError, callback: handleCreateTableView } = useApiCallback(
    async (name: string, configuration: TableViewConfiguration) => {
      const responseTableView = await SessionClientDashboard.postResourceCreate<CasesTableView>({
        attributes: {
          name,
          configuration,
          'table-key': TableKey.CasesIndex,
        },
        type: ApiModel.TableView,
      });

      props.setTableViews({
        tableViews: sortBy(
          (tableView) => tableView.attributes.name,
          [...props.tableViews, responseTableView.data],
        ),
      });
      props.setCurrentTableViewId({ currentTableViewId: responseTableView.data.id });
      props.showMessage('Successfully created Cases view.', MessageType.Success);
    },
    [props.tableViews],
  );

  useApiErrorHandler({
    error: createTableViewError,
    onError: (error) => {
      if (isPermissionsError(error)) {
        props.showMessage(
          "You don't have permission to create a new table view.",
          MessageType.Failure,
        );
      } else {
        props.showMessage(
          'Failed to create table view, please try again.',
          MessageType.Failure,
          false,
        );
      }
    },
    onNetworkError: () =>
      props.showMessage(
        'We are experiencing network issues. Please try again.',
        MessageType.Failure,
        false,
      ),
  });

  const { error: saveTableViewError, callback: handleSaveTableView } = useApiCallback(
    async (id: string, name: string, configuration: TableViewConfiguration) => {
      const responseTableView = await SessionClientDashboard.updateResource<CasesTableView>({
        attributes: {
          name,
          configuration,
        },
        id,
        type: ApiModel.TableView,
      });
      const nextTableViews = [...props.tableViews];
      const tableViewToUpdateIndex = props.tableViews.findIndex(
        (view: CasesTableView) => view.id === id,
      );
      nextTableViews.splice(tableViewToUpdateIndex, 1, responseTableView.data);
      props.setTableViews({ tableViews: nextTableViews });
      props.setCurrentTableViewId({ currentTableViewId: responseTableView.data.id });

      props.showMessage('Successfully created Cases view.', MessageType.Success);
    },
    [props.tableViews],
  );

  useApiErrorHandler({
    error: saveTableViewError,
    onError: (error) => {
      if (isPermissionsError(error)) {
        props.showMessage(permissionsErrorMessage(error), MessageType.Failure);
      } else {
        props.showMessage(
          'Failed to save table view, please try again.',
          MessageType.Failure,
          false,
        );
      }
    },
    onNetworkError: () =>
      props.showMessage(
        'We are experiencing network issues. Please try again.',
        MessageType.Failure,
        false,
      ),
  });

  const { error: handleArchiveTableViewError, callback: handleArchiveTableView } = useApiCallback(
    async (id: string) => {
      await SessionClientDashboard.deleteResource<CasesTableView>(ApiEndpoint.TableView, id);
      loadCaseTableViews();
      props.showMessage('Successfully deleted table view.', MessageType.Success);
    },
    [loadCaseTableViews],
  );

  useApiErrorHandler({
    error: handleArchiveTableViewError,
    onError: (error) => {
      if (isPermissionsError(error)) {
        props.showMessage(permissionsErrorMessage(error), MessageType.Failure);
      } else if (error.status === 400) {
        props.showMessage(
          'Failed to delete table view. Last table view cannot be deleted',
          MessageType.Failure,
        );
      } else {
        props.showMessage(
          'Failed to delete table view, please try again.',
          MessageType.Failure,
          false,
        );
      }
    },
    onNetworkError: () =>
      props.showMessage(
        'Failed to delete table view, please try again.',
        MessageType.Failure,
        false,
      ),
  });
  const handleSetCurrentTableView = (id: string) => {
    const tableView = props.tableViews.find((tableView) => tableView.id === id);

    if (!tableView) {
      return;
    }

    props.setCurrentTableViewId({ currentTableViewId: tableView.id });

    props.setFilters({
      ...generateCaseFiltersFromTableView(tableView.attributes.configuration.filters),
      isInitial: false,
    });
    if (tableView.attributes.configuration.sort) {
      props.setSortParams({
        sort: tableView.attributes.configuration.sort.sort,
        shouldSortDescending: tableView.attributes.configuration.sort['should-sort-descending'],
      });
    }
  };

  const handleClearFilters = () => {
    props.setFilters({
      caseStateNames: [],
      /* @ts-expect-error [strict-migration] The following line is not strict; please fix! */
      startDate: null,
      /* @ts-expect-error [strict-migration] The following line is not strict; please fix! */
      endDate: null,
      assigneeIds: [],
      notAssigneeIds: [],
      teamAssigneeIds: [],
      caseTemplateIds: [],
      caseQueueIds: [],
      tagsConditions: [],
      omni: '',
    });
  };

  const hasCasesFree = !productFeatureDisabled(
    props.organization,
    ProductFeatureName.CasesFree,
    props.currentEnvironment.type,
  );
  const hasCasesBasic = !productFeatureDisabled(
    props.organization,
    ProductFeatureName.CasesBasic,
    props.currentEnvironment.type,
  );
  const hasCasesEnhanced = !productFeatureDisabled(
    props.organization,
    ProductFeatureName.CasesEnhanced,
    props.currentEnvironment.type,
  );
  const hasCasesEnterprise = !productFeatureDisabled(
    props.organization,
    ProductFeatureName.CasesEnterprise,
    props.currentEnvironment.type,
  );

  return (
    <DashboardCasesView
      organization={props.organization}
      loadingCases={loadingCases}
      loadingTableViews={loadingTableViews}
      loadingTags={loadingTags}
      loadingCaseTemplateBasicsOptions={loadingCaseTemplateBasicsOptions}
      productFeatureIsDisabled={productFeatureIsDisabled}
      exportersProductFeatureIsDisabled={exportersProductFeatureIsDisabled}
      accounts={props.accounts}
      allTags={props.tags}
      casesTags={props.casesTags}
      caseQueues={props.caseQueues}
      users={users ?? []}
      activeTeams={props.teams.filter((team) => team.attributes.status === 'active')}
      cases={props.cases}
      userNotifications={{
        [INBOX_REQUEST_KEY]: props.userNotifications[INBOX_REQUEST_KEY],
        [ARCHIVED_REQUEST_KEY]: props.userNotifications[ARCHIVED_REQUEST_KEY],
      }}
      activeCaseTemplateBasicsOptions={props.activeCaseTemplateBasicsOptions}
      caseUsers={props.caseUsers}
      caseCaseQueues={props.caseCaseQueues}
      /* @ts-expect-error [strict-migration] The following line is not strict; please fix! */
      paginationParams={props.paginationParams}
      /* @ts-expect-error [strict-migration] The following line is not strict; please fix! */
      sortParams={props.sortParams}
      filters={props.filters}
      /* @ts-expect-error [strict-migration] The following line is not strict; please fix! */
      pageSize={props.pageSize}
      /* @ts-expect-error [strict-migration] The following line is not strict; please fix! */
      currentTableView={props.currentTableView}
      activeTableViews={props.tableViews.filter(
        (view) => view.attributes.status === TableViewStatus.Active,
      )}
      /* @ts-expect-error [strict-migration] The following line is not strict; please fix! */
      impersonationMode={props.impersonationMode}
      hasCasesFree={hasCasesFree}
      hasCasesBasic={hasCasesBasic}
      hasCasesEnhanced={hasCasesEnhanced}
      hasCasesEnterprise={hasCasesEnterprise}
      handleCreateCase={handleCreateCase}
      handleCreateTableView={handleCreateTableView}
      handleClearFilters={handleClearFilters}
      handleSaveTableView={handleSaveTableView}
      handleSetCurrentTableView={handleSetCurrentTableView}
      handleArchiveTableView={handleArchiveTableView}
      handleExportData={handleExportData}
      handlePageLinkClick={handlePageLinkClick}
      handlePageSizeClick={handlePageSizeClick}
      handleDateUpdate={handleDateUpdate}
      handleDateUpdateV2={handleDateUpdateV2}
      handleDateTypeFilterUpdate={handleDateTypeFilterUpdate}
      handleOmniSearch={handleOmniSearch}
      handleSortUpdate={handleSortUpdate}
      handleStateFilterUpdate={handleStateFilterUpdate}
      handleCaseTemplateFilterUpdate={handleCaseTemplateFilterUpdate}
      handleCaseQueueFilterUpdate={handleCaseQueueFilterUpdate}
      handleAssigneeFilterUpdate={handleAssigneeFilterUpdate}
      handleNotAssigneeFilterUpdate={handleNotAssigneeFilterUpdate}
      handleTagsConditionsFilterUpdate={handleTagsConditionsFilterUpdate}
      handleBulkAssign={handleBulkAssign}
      handleBulkSetState={handleBulkSetState}
      markAllNotificationsAsReceived={markAllNotificationsAsReceived}
      archiveNotification={archiveNotification}
      unarchiveNotification={unarchiveNotification}
      archiveAllNotifications={archiveAllNotifications}
      loadUserNotificationPage={loadUserNotificationPage}
    />
  );
};

const mapStateToProps = (state: RootState) => {
  return {
    activeCaseTemplateBasicsOptions: state.caseTemplates.basicsOptions.filter(
      (option) => option.status === CaseTemplateStatus.Active,
    ),
    accounts: state.cases.accounts,
    cases: state.cases.caseRequestKey ? state.cases.cases[state.cases.caseRequestKey] || [] : [],
    casesTags: state.cases.tags,
    caseUsers: state.cases.users,
    caseCaseQueues: state.cases.caseQueues,
    caseQueues: state.caseQueues.caseQueues,
    currentEnvironment: state.currentEnvironment.currentEnvironment,
    currentPage: state.cases.currentPage,
    currentTableView: state.tableViews.tableViews.find(
      (tv) => tv.id === state.tableViews.currentTableViewId,
    ),
    filters: getAdjustedCaseFilters(state),
    impersonationMode: state.currentUser.impersonationMode,
    organization: state.organization.organization,
    pageSize: state.cases.pageSize,
    paginationParams: state.cases.paginationParams,
    sortParams: state.cases.sortParams,
    tableViews: state.tableViews.tableViews,
    tags: state.tags.tags.case,
    teams: state.teams.teams,
    userNotifications: state.userNotifications.userNotifications,
  };
};

const mapDispatchToProps = {
  loadUserNotificationsListAdd,
  createCase,
  clearUserNotifications,
  loadTeams,
  loadUserNotificationsList,
  loadCasesList,
  loadCaseTemplateBasicsOptions,
  loadCaseQueuesList,
  loadExporterCase,
  loadTags,
  loadTableViews,
  saveUserNotifications,
  setSortParams,
  setFilters,
  setCurrentPage,
  setPageSize,
  showMessage,
  setCurrentTableViewId,
  setTableViews,
};

const connector = connect(mapStateToProps, mapDispatchToProps);
type PropsFromRedux = ConnectedProps<typeof connector>;

export default connector(DashboardCasesRoute);
